import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  ENVIRONMENT,
  CreditPullType,
  CreditPullFeatureFlags,
  CreditAuthorizationWithPullType,
  CreditScoreResult,
  CreditBureauScore,
  SoftPullInfoBanner,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

import { MlaAuthService } from './mla-auth.service';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class CreditPullService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly loggingService = inject(LoggingService);

  readonly selectedPullType = signal<CreditPullType | null>(null);

  readonly featureFlags = signal<CreditPullFeatureFlags>({
    softPullOnly: false,
    softPullAndTrimerge: false,
    hardPullEnabled: true,
  });

  readonly creditScores = signal<CreditScoreResult | null>(null);

  readonly isAuthorizing = signal(false);

  readonly infoBanner = signal<SoftPullInfoBanner>({
    icon: 'info',
    message: '',
    show: false,
  });

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  async loadFeatureFlags(loanId: string): Promise<CreditPullFeatureFlags> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}/feature-flags`;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: Record<string, boolean> }>(url)
      );

      if (response?.success && response.data) {
        const flags: CreditPullFeatureFlags = {
          softPullOnly: response.data['SoftPullOnly'] === true,
          softPullAndTrimerge: response.data['SoftPullAndTrimerge'] === true,
          hardPullEnabled: response.data['HardPullEnabled'] !== false,
        };
        this.featureFlags.set(flags);
        return flags;
      }
    } catch (error) {
      this.loggingService.logException(error, '[CreditPullService] Error loading feature flags:');
    }

    return this.featureFlags();
  }

  isSoftPullOnly(): boolean {
    return this.featureFlags().softPullOnly;
  }

  isTrimergeAvailable(): boolean {
    return this.featureFlags().softPullAndTrimerge;
  }

  getAvailablePullTypes(): CreditPullType[] {
    const flags = this.featureFlags();
    const types: CreditPullType[] = [];

    if (flags.softPullOnly) {
      types.push('soft');
    } else {
      if (flags.hardPullEnabled) {
        types.push('hard');
      }
      if (flags.softPullAndTrimerge) {
        types.push('soft', 'triMerge');
      }
    }

    return types.length > 0 ? types : ['hard'];
  }

  selectPullType(pullType: CreditPullType): void {
    this.selectedPullType.set(pullType);
    this.updateInfoBanner(pullType);
  }

  private updateInfoBanner(pullType: CreditPullType): void {
    switch (pullType) {
      case 'soft':
        this.infoBanner.set({
          icon: 'info',
          message:
            'A soft credit pull will not affect your credit score. This is used for prequalification purposes only.',
          show: true,
        });
        break;
      case 'hard':
        this.infoBanner.set({
          icon: 'warning',
          message:
            'A hard credit pull may temporarily affect your credit score. This is required for final loan approval.',
          show: true,
        });
        break;
      case 'triMerge':
        this.infoBanner.set({
          icon: 'info',
          message:
            'A tri-merge credit report pulls data from all three credit bureaus for a comprehensive view.',
          show: true,
        });
        break;
      default:
        this.infoBanner.set({ icon: 'info', message: '', show: false });
    }
  }

  async authorizeCreditPull(request: CreditAuthorizationWithPullType): Promise<CreditScoreResult | null> {
    this.isAuthorizing.set(true);

    const url = `${this.mlaApiUrl}api/v1/credit/authorize`;

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: CreditScoreResult; errors?: string[] }>(url, request)
      );

      if (response?.success && response.data) {
        this.creditScores.set(response.data);
        return response.data;
      }

      this.loggingService.logWarning('[CreditPullService] Authorization failed', { errors: JSON.stringify(response?.errors) });
      return null;
    } catch (error) {
      this.loggingService.logException(error, '[CreditPullService] Error authorizing credit pull:');
      return null;
    } finally {
      this.isAuthorizing.set(false);
    }
  }

  async getCreditScores(borrowerId: string, loanId: string): Promise<CreditScoreResult | null> {
    const url = `${this.mlaApiUrl}api/v1/credit/scores`;
    const params = { borrowerId, loanId };

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: CreditScoreResult }>(url, { params })
      );

      if (response?.success && response.data) {
        this.creditScores.set(response.data);
        return response.data;
      }
    } catch (error) {
      this.loggingService.logException(error, '[CreditPullService] Error getting credit scores:');
    }

    return null;
  }

  hasScores(): boolean {
    const scores = this.creditScores();
    return scores !== null && scores.hasScores;
  }

  getMiddleScore(): number | null {
    const result = this.creditScores();
    if (!result?.scores?.length) {
      return null;
    }

    const availableScores = result.scores
      .filter((s: CreditBureauScore) => s.available && s.score !== undefined)
      .map((s: CreditBureauScore) => s.score as number)
      .sort((a: number, b: number) => a - b);

    if (availableScores.length === 0) {
      return null;
    }

    if (availableScores.length === 1) {
      return availableScores[0];
    }

    if (availableScores.length === 2) {
      return Math.min(...availableScores);
    }

    return availableScores[1];
  }

  reset(): void {
    this.selectedPullType.set(null);
    this.creditScores.set(null);
    this.isAuthorizing.set(false);
    this.infoBanner.set({ icon: 'info', message: '', show: false });
  }
}
